import { debounce } from 'lodash';
import { Node, NodeType } from 'prosemirror-model';
import { Plugin, PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

import {
  getProseMirrorDocumentDuration,
  getProseMirrorDocumentMinimumDuration,
} from './prosemirrorDocumentDuration';
import { editorStats } from './types';

export const editorStatskey = new PluginKey('editor-stats-plugin');

const EMPTY_OBJ_READ_ONLY: Record<never, never> = {};

const getWordsInText = (textContent: string) => {
  return textContent.split(' ').length;
};

export const getBlockWords = (node: Node) => {
  // @ts-ignore
  const childNodes = [...node.content.content];
  let words = 0;
  let characters = 0;
  for (let childNode of childNodes) {
    if (childNode.isTextblock) {
      words += getWordsInText(childNode.textContent);
      characters += childNode.textContent.length;
    } else if (childNode.childCount) {
      childNodes.push(...childNode.content.content);
    }
  }
  return { words, characters };
};

const getInitialProps = () => {
  return {
    characters: 0,
    words: 0,
    paragraphs: 0,
    blocks: 0,
    duration: 0,
    minDuration: 0,
  };
};

const arePropsSame = (oldProps: editorStats, newProps: editorStats | Record<never, never>) => {
  return JSON.stringify(oldProps) !== JSON.stringify(newProps);
};

const getBlockCount = (docViewNode: Node) => {
  return docViewNode.childCount;
};

const getParagraphsCount = (docViewNode: Node, paragraphNodeType: NodeType) => {
  // @ts-ignore
  return docViewNode.content.content.filter((node: Node) => node.type === paragraphNodeType).length;
};

const getNewEditorStateProps = (view: EditorView) => {
  // @ts-ignore
  const docViewNode = view.docView?.node;

  if (!docViewNode) {
    return EMPTY_OBJ_READ_ONLY;
  }

  const props = getInitialProps();
  const nodes = view.state.schema.nodes;

  props.blocks = getBlockCount(docViewNode);
  props.paragraphs = getParagraphsCount(docViewNode, nodes.paragraph);

  const { words, characters } = getBlockWords(docViewNode);
  props.words = words;
  props.characters = characters;

  props.duration = getProseMirrorDocumentDuration({ proseMirrorDocument: docViewNode });
  props.minDuration = getProseMirrorDocumentMinimumDuration({ proseMirrorDocument: docViewNode });

  return props;
};

export const editorStatsPlugin = () => {
  return new Plugin({
    key: editorStatskey,
    state: {
      init() {
        return getInitialProps();
      },
      apply(tr, old) {
        const props = tr.getMeta(editorStatskey);
        return props || old;
      },
    },
    view: () => {
      return {
        update: debounce((view) => {
          // @ts-ignore
          const docView = view.docView;
          if (!docView) {
            return;
          }
          const newProps = getNewEditorStateProps(view);
          const oldProps = editorStatskey.getState(view.state);

          if (arePropsSame(oldProps, newProps)) {
            view.dispatch(view.state.tr.setMeta(editorStatskey, newProps));
          }
        }, 2000),
      };
    },
  });
};
